/* Recipe V2 API data types */

export enum RecipeStatus {
  Draft = 'Draft',
  Scheduled = 'Scheduled',
  Active = 'Active',
  Expired = 'Expired',
  Archived = 'Archived',
}

export enum AllergenStatus {
  Contains = 'Contains',
  MayContain = 'MayContain',
  NotPresent = 'NotPresent',
  FreeFrom = 'FreeFrom',
  Unknown = 'Unknown',
}

export enum AllergenSource {
  Manual = 'Manual',
  Feed = 'Feed',
  AI = 'AI',
  // Real values returned by the IM API (RecipeQueryService/RecipesController), distinct from the mock-only values above.
  Supplier = 'Supplier',
  RecipeOverride = 'RecipeOverride',
}

export enum DietaryCategory {
  Allergen = 'Allergen',
  Intolerance = 'Intolerance',
  DietaryRequirement = 'DietaryRequirement',
  LifestylePreference = 'LifestylePreference',
  NutritionalClaim = 'NutritionalClaim',
}

export enum DietaryAttributeStatus {
  Contains = 'Contains',
  MayContain = 'MayContain',
  FreeFrom = 'FreeFrom',
  Suitable = 'Suitable',
  NotSuitable = 'NotSuitable',
  Certified = 'Certified',
  Unknown = 'Unknown',
}

export interface DietaryAttribute {
  id: number;
  code: string;
  name: string;
  category: DietaryCategory;
  status: DietaryAttributeStatus;
  source: AllergenSource;
  iconUrl?: string;
  regulatory?: boolean;
  /** Pre-override, calculated status - only present when isOverridden is true. */
  originalStatus?: DietaryAttributeStatus;
  originalSource?: AllergenSource;
  overrideStatus?: DietaryAttributeStatus;
  overrideSource?: AllergenSource;
  isOverridden?: boolean;
}

export enum ApprovalStatus {
  Draft = 'Draft',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum AIFindingSeverity {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export enum AIFindingStatus {
  Open = 'Open',
  Dismissed = 'Dismissed',
  Acknowledged = 'Acknowledged',
}

export enum CarbonRating {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
}

/**
 * Standard recipes are sold/served as-is. Batch recipes (e.g. house sauces, doughs) are
 * prepared in bulk and held as stock - see `ConvertToStockRequest`/`convertToStock`.
 */
export enum RecipeKind {
  Standard = 'Standard',
  Batch = 'Batch',
}

export interface RecipeYield {
  id: number;
  quantity: number;
  uom: string;
  displayOrder: number;
}

export interface RecipeYieldRequest {
  quantity: number;
  uom: string;
  displayOrder: number;
}

export interface RecipeV2 {
  id: number;
  franchiseId: number;
  itemNumber: string;
  /** Canonical inventory UOM for produced-stock receipt/depletion. */
  stockUom?: string | null;
  description: string;
  narrativeDescription?: string | null;
  status: RecipeStatus;
  primaryCategory: string;
  yields: RecipeYield[];
  /** Not yet backed by the real API - populated only by the mocked client. */
  recipeType?: RecipeKind;
  productionMethodId?: number | null;
  yieldPortions?: number | null;
  portionQuantity?: number | null;
  portionUom?: string | null;
  expectedYieldLossPercent?: number | null;
  consumptionMode?: 'PerPortion' | 'WholeYield' | 'ProducedStock';
  productionTrigger?: 'Automatic' | 'Manual' | null;
  storageMethod?: string | null;
  shelfLifeValue?: number | null;
  shelfLifeUnit?: string | null;
  carbonScore?: CarbonRating | null;
  totalCost?: number;
  costPerPortion?: number;
  costIsComplete?: boolean;
  missingCostComponentItemNumbers?: string[];
  mediaUrl?: string | null;
  recipeVersionId?: number | null;
  recipePublicationId?: number | null;
  /** Effective compliance revisions contributing to this governed audience snapshot. */
  complianceRevisionIds?: number[];
  effectiveFrom?: string;
  effectiveTo?: string;
  /** Loaded only for recipe detail or expanded list rows. */
  components?: RecipeComponent[];
  /** Direct component count from lightweight list projection. */
  componentCount?: number;
  nutrition?: RecipeNutrition[];
  allergenSummary?: RecipeAllergenSummary[];
  dietaryAttributes?: DietaryAttribute[];
  aiIssueCount?: number;
  currentVersion?: number;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
}

export interface RecipeComponent {
  id: number;
  recipeId: number;
  componentProductId: number;
  componentRole?: 'Ingredient' | 'Packaging' | 'Operational' | 'NestedRecipe';
  componentItemNumber: string;
  componentDescription: string;
  quantity: number;
  weight: number | null;
  weightSource?: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  volume: number;
  volumeSource?: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  /** Client ordering state. Component response order is authoritative on reload. */
  sortOrder: number;
  uom: string;
  costPrice: number | null;
  costContribution: number | null;
  costIsComplete?: boolean;
  missingCostComponentItemNumbers?: string[];
  weightNet: number;
  isRecipe: boolean;
  children?: RecipeComponent[];
  allergens?: ProductAllergen[];
  dietaryAttributes?: DietaryAttribute[];
  nutrition?: RecipeNutrition[];
  carbonFactor?: CarbonFactor | null;
}

export interface RecipeAllergenSummary {
  allergenId: number;
  allergenCode: string;
  allergenName: string;
  status: AllergenStatus;
  source: AllergenSource;
  introducedBy: AllergenTraceEntry[];
}

export interface AllergenTraceEntry {
  componentId: number;
  componentDescription: string;
  depth: number;
  path: string;
}

export interface Allergen {
  id: number;
  code: string;
  name: string;
  iconUrl: string;
}

export interface ProductAllergen {
  productId: number;
  allergenId: number;
  allergenCode: string;
  allergenName: string;
  status: AllergenStatus;
  source: AllergenSource;
  updatedBy: string;
  updatedAt: string;
}

export interface CarbonFactor {
  productId: number;
  kgCo2ePerUnit: number;
  source: string;
  sourceReference: string;
  lastUpdated: string;
}

export interface RecipeVersion {
  id: number;
  recipeId: number;
  versionNumber: number;
  changeDescription: string;
  changedBy: string;
  changedAt: string;
  approvalStatus: ApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  componentSnapshot: RecipeComponent[];
}

export interface RecipeVersionHistory {
  id: number;
  recipeId: number;
  versionNumber: number;
  status: string;
  versionOrigin: string;
  basedOnRecipeVersionId: number | null;
  changeReason: string | null;
  createdBy: number | null;
  createdDate: string;
  modifiedBy: number | null;
  modifiedDate: string | null;
  latestDecision: string | null;
  decisionBy: number | null;
  decisionDate: string | null;
  decisionReason: string | null;
}

export interface RecipeVersionComponentSnapshot {
  id: number;
  componentKey: string;
  componentItemId: number;
  componentRole: 'Ingredient' | 'Packaging' | 'Operational' | 'NestedRecipe';
  nestedRecipeVersionId: number | null;
  quantity: number;
  uom: string;
  weight: number | null;
  weightSource: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  volume: number | null;
  volumeSource: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  sortOrder: number | null;
}

export interface RecipeVersionPublication {
  id: number;
  recipeId: number;
  recipeVersionId: number;
  scopeType: string;
  scopeId: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
  createdBy: number | null;
  createdDate: string;
  publishedBy: number | null;
  publishedDate: string | null;
  withdrawnBy: number | null;
  withdrawnDate: string | null;
}

export interface RecipeVersionDetail {
  version: RecipeVersionHistory;
  productionMethodId: number | null;
  yieldPortions: number | null;
  portionQuantity: number | null;
  portionUom: string | null;
  expectedYieldLossPercent: number | null;
  consumptionMode: 'PerPortion' | 'WholeYield' | 'ProducedStock';
  productionTrigger: 'Automatic' | 'Manual' | null;
  storageMethod: string | null;
  shelfLifeValue: number | null;
  shelfLifeUnit: string | null;
  description: string | null;
  primaryCategory: string | null;
  narrativeDescription: string | null;
  heroMediaReference: string | null;
  publication: RecipeVersionPublication | null;
  components: RecipeVersionComponentSnapshot[];
}

export interface AIFinding {
  id: number;
  recipeId: number;
  findingType: string;
  severity: AIFindingSeverity;
  description: string;
  confidence: number;
  status: AIFindingStatus;
  dismissedBy: string | null;
  dismissedReason: string | null;
  createdAt: string;
}

export interface RecipeV2Detail extends RecipeV2 {
  /** Not yet backed by the real API - populated only by the mocked client. */
  components?: RecipeComponent[];
  allergens?: RecipeAllergenSummary[];
  versions?: RecipeVersion[];
  aiFindings?: AIFinding[];
  carbonTotal?: number;
  carbonPerPortion?: number;
  productionMethod?: ProductionMethod | null;
  steps?: RecipeStep[];
}

export interface RecipeAudienceDetail {
  id: number;
  franchiseId: number;
  itemNumber: string;
  description: string;
  narrativeDescription?: string | null;
  primaryCategory: string;
  mediaUrl?: string | null;
  recipeVersionId?: number | null;
  recipePublicationId?: number | null;
  complianceRevisionIds: number[];
  currentVersion?: number;
  nutrition: RecipeNutrition[];
  allergenSummary: RecipeAllergenSummary[];
  dietaryAttributes: DietaryAttribute[];
}

export interface RecipeV2ListResponse {
  data: RecipeV2[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CostBreakdownEntry {
  componentId: number;
  componentDescription: string;
  costPrice: number;
  quantity: number;
  totalCost: number;
  percentageOfTotal: number;
}

export interface CostSimulationResult {
  originalCost: number;
  simulatedCost: number;
  difference: number;
  percentageChange: number;
  originalCostPerPortion?: number | null;
  simulatedCostPerPortion?: number | null;
}

export interface RecipeNutrition { code: string; name: string; value: number | null; valueUom?: string | null; basisType?: string | null; basisQuantity?: number | null; basisUom?: string | null; servingSize?: number | null; servingUom?: string | null; perServing?: number | null; referenceIntakePercent?: number | null; originalValue?: number | null; originalValueUom?: string | null; overrideValue?: number | null; overrideValueUom?: string | null; overrideNotes?: string | null; isOverridden?: boolean; overrideCreatedBy?: number | null; overrideCreatedDate?: string | null; overrideModifiedBy?: number | null; overrideModifiedDate?: string | null; }

export interface NutritionOverride {
  nutrientCode: string;
  value: number;
  valueUom?: string | null;
  notes: string;
}

export interface NutrientReference {
  nutrientId: number;
  code: string;
  name: string;
}

export interface RecipeMenuUsage {
  menuId: number;
  franchiseId: number;
  menuName: string;
  rangeNames: string[];
  status: 'Draft' | 'Scheduled' | 'Active' | 'Expired';
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  displayOrder: number;
}

export interface SupplierPrice {
  supplierId: number;
  supplierName: string;
  itemPrice: number;
  packSize: number;
  packUom: string;
  costPerUnit: number;
  isPreferred: boolean;
}

export interface ComponentSupplierComparison {
  componentId: number;
  componentDescription: string;
  suppliers: SupplierPrice[];
}

export interface RecipeOperationalConfigurationRequest {
  yieldPortions?: number | null;
  portionQuantity?: number | null;
  portionUom?: string | null;
  expectedYieldLossPercent?: number | null;
  consumptionMode: 'PerPortion' | 'WholeYield' | 'ProducedStock';
  productionTrigger?: 'Automatic' | 'Manual' | null;
  storageMethod?: string | null;
  shelfLifeValue?: number | null;
  shelfLifeUnit?: string | null;
}

export interface CreateRecipeRequest extends RecipeOperationalConfigurationRequest {
  franchiseId: number;
  itemNumber: string;
  description: string;
  primaryCategory: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  productionMethodId?: number | null;
  narrativeDescription?: string | null;
}

export interface ProductionMethod {
  id: number;
  franchiseId: number;
  name: string;
  isActive: boolean;
  sortOrder: number;
  /** Mock-only carbon factor - not backed by the real API. */
  carbonMultiplier?: number;
  /** Mock-only description - not backed by the real API. */
  description?: string;
}

export interface CreateComponentRequest {
  componentProductId: number;
  componentItemNumber: string;
  componentDescription: string;
  quantity: number;
  weight: number | null;
  volume: number | null;
  uom: string;
  sortOrder: number;
}

export interface ApproveRejectRequest {
  comment: string;
}

export interface AllergenOverride {
  allergenId: number;
  overrideStatus: AllergenStatus;
  reason: string;
}

export interface RecipeExportRequest {
  recipeIds: number[];
  format: 'csv' | 'xlsx';
}

export type RecipeExportInformationProfile = 'Operational' | 'Training' | 'Compliance';
export type RecipeExportPresentationStyle = 'Kitchen' | 'PrintFriendly' | 'FullColour' | 'Compact';

export type RecipeBulkOperation = 'Create' | 'Update' | 'NoChange' | 'Archive' | 'Submit' | 'Approve' | 'Publish' | string;

export interface RecipeBulkImportRow {
  id: number;
  sourceSection: string;
  rowNumber: number;
  recipeItemNumber?: string | null;
  recipeId?: number | null;
  recipeVersionId?: number | null;
  sourceFingerprint?: string | null;
  operation?: RecipeBulkOperation | null;
  resultStatus: string;
  errorCode?: string | null;
  errorMessage?: string | null;
}

export interface RecipeBulkImportRun {
  id: number;
  franchiseId: number;
  idempotencyKey: string;
  contentHash?: string | null;
  status: string;
  createdBy: number;
  createdDate: string;
  completedDate?: string | null;
  rows: RecipeBulkImportRow[];
}

export interface StockItemSearchResult {
  id: number;
  itemNumber: string;
  description: string;
  uom: string;
  costPrice: number;
  weightNet: number;
  /** True when this result is a recipe (sub-recipe) rather than a raw stock item. */
  isRecipe?: boolean;
}

export interface ConvertToStockRequest {
  quantity: number;
  uom: string;
  /**
   * Stable per user production attempt so a retry cannot create a duplicate
   * inventory production transaction.
   */
  idempotencyKey?: string;
}

export interface ConvertToStockResult {
  transactionId: number;
  eventReference: string;
  recipeId: number;
  recipeVersionId: number;
  recipePublicationId: number | null;
  quantity: number;
  uom: string;
  recipeExecutions: number;
  storageMethod: string;
  shelfLifeValue: number;
  shelfLifeUnit: string;
  expectedYieldLossPercent: number | null;
}

export interface Equipment {
  id: number;
  name: string;
  description?: string;
}

export interface RecipeStep {
  id: number;
  recipeId: number;
  stepNumber: number;
  title: string;
  instruction: string;
  productionMethodId: number | null;
  durationMinutes: number | null;
  temperature: string | null;
  equipment: Equipment[];
  mediaUrl?: string | null;
}

export interface RecipeMediaUploadAuthorization {
  uploadId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface CreateUpdateStepRequest {
  /** Legacy mock compatibility; server derives order. */
  stepNumber?: number;
  title: string;
  instruction: string;
  productionMethodId: number | null;
  durationMinutes: number | null;
  temperature: string | null;
  equipmentNames: string[];
  /** Legacy mock compatibility; production API persists equipment names. */
  equipmentIds?: number[];
}

export interface UpdateComponentRequest {
  componentProductId?: number;
  componentRole?: 'Ingredient' | 'Packaging' | 'Operational' | 'NestedRecipe';
  quantity: number;
  weight?: number | null;
  volume?: number | null;
  uom: string;
  sortOrder: number;
}

export interface UpdateRecipeMetadataRequest extends RecipeOperationalConfigurationRequest {
  description: string;
  primaryCategory: string;
  narrativeDescription?: string | null;
  effectiveFrom?: string;
  effectiveTo?: string;
  productionMethodId?: number | null;
  changeReason?: string | null;
}

export interface CloneRecipeRequest {
  itemNumber: string;
  description: string;
}

export interface DietaryAttributeOverride {
  attributeId: number;
  status: DietaryAttributeStatus;
  reason: string;
}

export interface DietaryAttributeReference {
  id: number;
  code: string;
  name: string;
  category: DietaryCategory;
}
